import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  DigitalCase,
  DigitalQuestion,
  DigitalSection,
  DigitalSubmission,
  DigitalSubmissionMeta,
  FullSubmissionMap,
  QuestionCaseSetting,
} from '../../../../redux/types';
import AnswerSummary from '../../../digitalTrial/AnswerSummary';
import usePendingCompulsoryQuestions from '../../../../hooks/survey/usePendingCompulsoryQuestions';
import usePendingCompulsoryCtxQuestions from '../../../../hooks/survey/usePendingCompulsoryCtxQuestions';

type Props = {
  cases: DigitalCase[];
  questions: DigitalQuestion[];
  contextQuestions: DigitalQuestion[];
  sections: DigitalSection[];
  selectedSection?: DigitalSection;
  questionCaseSettings: QuestionCaseSetting[];
  fullSubmissionMap: FullSubmissionMap;
  settingsDictionary: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  fullCtxSubmissionMap: Record<number, Record<number, DigitalSubmission>>;
  questionCaseSubmissionMap: Record<number, Record<number, DigitalSubmission> | undefined>;
  onSectionClick: (event: React.SyntheticEvent | null, newValue: number) => void
  onCaseClick: ((questionId: number, caseId: number) => void) | undefined
  onNavigateCtxQuestion: (qId: number) => void;
  onSubmit: () => void;
  dsms?: DigitalSubmissionMeta[];
  onHighlightOptout: () => void;
  isAllSaved: boolean;
};

const SubmissionDialog: React.FC<Props> = ({
  cases,
  questions,
  contextQuestions,
  sections,
  selectedSection,
  questionCaseSettings: settings,
  questionCaseSubmissionMap,
  fullSubmissionMap,
  fullCtxSubmissionMap,
  settingsDictionary,
  dsms,
  onCaseClick,
  onSectionClick,
  onNavigateCtxQuestion,
  onSubmit,
  onHighlightOptout,
  isAllSaved,
}: Props) => {
  const [incompleteSettingMap, pendingSecId] = usePendingCompulsoryQuestions(
    settings, fullSubmissionMap, dsms,
  );
  const [incompleteCtxSettingMap, pendingCtxSecId] = usePendingCompulsoryCtxQuestions(
    settings, fullCtxSubmissionMap, dsms,
  );

  const [selectedDsm, setSelectedDsm] = useState<DigitalSubmissionMeta | undefined>(undefined);

  useEffect(() => {
    const pendingSectionId = pendingSecId !== undefined ? pendingSecId : pendingCtxSecId;
    if (!pendingSectionId) {
      return;
    }
    onSectionClick(null, pendingSectionId);
  }, [onSectionClick, pendingSecId, pendingCtxSecId]);

  useEffect(() => {
    if (selectedSection && dsms) {
      setSelectedDsm(dsms?.find((dsm) => dsm.selectedSectionId === selectedSection.id));
    }
  }, [dsms, selectedSection]);

  return (
    <Box sx={{ p: 3 }}>
      <Tabs
        sx={{ flexGrow: 1 }}
        value={selectedSection?.id}
        onChange={onSectionClick}
        variant="scrollable"
        scrollButtons
      >
        {
          sections.map((s) => <Tab label={s.name} key={s.id} value={s.id} />)
        }
      </Tabs>
      <Typography
        variant="h6"
        sx={{
          display: 'flex', justifyContent: 'space-between', m: 2, p: '0.5rem',
        }}
      >
        <FormattedMessage id="answerSummary.title" />
        {
          selectedSection && selectedSection.canOptOut && (
            <Box
              onClick={onHighlightOptout}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                cursor: 'pointer',
                p: '0.25rem',
                '&:hover': {
                  backgroundColor: 'ButtonFace',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                },
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                <FormattedMessage id="survey.labelOptout" />
                &nbsp;
              </Typography>
              {
                selectedDsm?.isOptOut ? (
                  <Typography variant="body1" sx={{ color: 'green', fontWeight: 'bold' }}>
                    <FormattedMessage id="yes" />
                  </Typography>
                )
                  : (
                    <Typography variant="body1" sx={{ color: 'red', fontWeight: 'bold' }}>
                      <FormattedMessage id="no" />
                    </Typography>
                  )
              }
            </Box>
          )
        }
      </Typography>
      <AnswerSummary
        digitalCases={cases}
        digitalQuestions={questions}
        submissionsMapByQuestionAndCaseId={questionCaseSubmissionMap}
        compulsorySettingsMapByQuestionAndCaseId={incompleteSettingMap}
        settingsDictionary={settingsDictionary}
        onCaseClick={onCaseClick}
      />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          <FormattedMessage id="review.methodicalQuestionTitle" />
        </Typography>
        <List>
          {
            contextQuestions.map((cq) => (
              <ListItem
                key={cq.id}
                disablePadding
                sx={incompleteCtxSettingMap?.[cq.id]?.compulsory ? { backgroundColor: '#fdeded' } : undefined}
              >
                <ListItemButton onClick={() => onNavigateCtxQuestion(cq.id)}>
                  <ListItemText primary={cq.text} />
                </ListItemButton>
              </ListItem>
            ))
          }
        </List>
      </Box>
      <Divider sx={{ my: 3 }} />
      {
        (incompleteSettingMap || incompleteCtxSettingMap) && (
          <Alert severity="error" sx={{ fontSize: '1.3rem', alignItems: 'center' }}>
            <FormattedMessage id="survey.compulsoryWarn" />
          </Alert>
        )
      }
      {
        !incompleteSettingMap && !incompleteCtxSettingMap && (
          <Alert severity="info" sx={{ fontSize: '1.3rem', alignItems: 'center' }}>
            <FormattedMessage id="survey.submitInfo" />
          </Alert>
        )
      }
      <Box sx={{ mt: 2 }} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          disabled={
            incompleteSettingMap !== undefined
            || incompleteCtxSettingMap !== undefined || !isAllSaved
          }
          onClick={onSubmit}
          startIcon={!isAllSaved && (
            <CircularProgress sx={{ color: 'primary.dark' }} size={18} />
          )}
        >
          <FormattedMessage id="registration.submit" />
        </Button>
      </Box>
    </Box>
  );
};

export default SubmissionDialog;
